import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import grupoMcpUsuarioService, { GrupoMcpUsuario } from '../../services/grupoMcpUsuarioService';
import grupoMcpService from '../../services/grupoMcpService';
import GrupoMcpSearch from '../GrupoMcpSearch';
import type { GrupoMcp } from '../../services/grupoMcpService';

interface GrupoConDescripcion extends GrupoMcpUsuario {
  descripcion?: string;
}

interface CajasUsuarioTabProps {
  usuario: string;
  /** Username exacto tal como se guarda en GrupoMcpUsuario (p. ej. desde formData.nombre) */
  username?: string;
  disabled?: boolean;
}

const CajasUsuarioTab: React.FC<CajasUsuarioTabProps> = ({ usuario, username, disabled }) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [grupos, setGrupos] = useState<GrupoConDescripcion[]>([]);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const usuarioParaConsulta = (username ?? usuario)?.trim();

  const fetchGrupos = useCallback(async () => {
    if (!usuario?.trim()) return;
    try {
      setLoading(true);
      let data = await grupoMcpUsuarioService.getByUsuario(usuarioParaConsulta);
      let items = Array.isArray(data) ? data : [];
      if (items.length === 0 && username && usuario.trim() !== usuarioParaConsulta) {
        data = await grupoMcpUsuarioService.getByUsuario(usuario.trim());
        items = Array.isArray(data) ? data : [];
      }
      const conDescripcion: GrupoConDescripcion[] = await Promise.all(
        items.map(async (g) => {
          try {
            const det = await grupoMcpService.getGrupoMcp(g.grupoMcp);
            return { ...g, descripcion: det?.descripcion };
          } catch {
            return { ...g };
          }
        })
      );
      setGrupos(conDescripcion);
    } catch (error: any) {
      console.error('Error al cargar grupos de caja del usuario:', error);
      enqueueSnackbar('Error al cargar grupos de caja asignados', { variant: 'error' });
      setGrupos([]);
    } finally {
      setLoading(false);
    }
  }, [usuarioParaConsulta, enqueueSnackbar]);

  useEffect(() => {
    fetchGrupos();
  }, [fetchGrupos]);

  const handleAddGrupo = async (grupoMcp: GrupoMcp) => {
    if (!usuarioParaConsulta) {
      enqueueSnackbar('No se puede asignar: falta identificar al usuario', { variant: 'warning' });
      return;
    }
    if (grupos.some((g) => g.grupoMcp === grupoMcp.grupoMcp)) {
      enqueueSnackbar('Este grupo ya está asignado al usuario', { variant: 'warning' });
      return;
    }
    try {
      await grupoMcpUsuarioService.create({
        grupoMcp: grupoMcp.grupoMcp,
        usuario: usuarioParaConsulta,
      });
      enqueueSnackbar('Grupo de caja asignado correctamente', { variant: 'success' });
      setAddDialogOpen(false);
      fetchGrupos();
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'Error al asignar grupo de caja';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleDelete = async (grupoMcp: number, usuarioKey: string) => {
    if (!window.confirm('¿Está seguro de eliminar esta asignación de grupo de caja?')) return;
    try {
      await grupoMcpUsuarioService.delete(grupoMcp, usuarioKey);
      enqueueSnackbar('Asignación eliminada correctamente', { variant: 'success' });
      fetchGrupos();
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'Error al eliminar asignación';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  if (disabled || !usuario?.trim()) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Guarde el usuario primero para poder asignar grupos de caja.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Grupos de caja asignados al usuario
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Asignar grupo de caja
        </Button>
      </Stack>

      {grupos.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          No hay grupos de caja asignados a este usuario.
        </Typography>
      ) : isMobile ? (
        <Stack spacing={2}>
          {grupos.map((g) => (
            <Card key={`${g.grupoMcp}-${g.usuario}`} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle2">
                      Grupo: {g.grupoMcp}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(g as GrupoConDescripcion).descripcion || '-'}
                    </Typography>
                  </Box>
                  <Tooltip title="Eliminar">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(g.grupoMcp, g.usuario)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grupos.map((g) => (
                <TableRow key={`${g.grupoMcp}-${g.usuario}`} hover>
                  <TableCell>{g.grupoMcp}</TableCell>
                  <TableCell>{(g as GrupoConDescripcion).descripcion || '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(g.grupoMcp, g.usuario)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <GrupoMcpSearch
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSelect={handleAddGrupo}
      />
    </Box>
  );
};

export default CajasUsuarioTab;
